import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArrowSeparateVertical, NavArrowUp, NavArrowDown } from 'iconoir-react';
import cloneDeep from 'lodash/cloneDeep';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { camelToKebab } from '../../../Scripts/Spotlight/Components/CamelToKebab';
import { anyCompare, stringCompare } from '../../../Scripts/Spotlight/Utilities/sort';
import { COLOR, SPACING } from '../../packages/constants';
import { LoadingSpinner } from '../loading-spinner';

export interface DataTableColumnDefinition {
    name: string;
    header: string;
    hideHeader?: boolean;
    dataId?: string;
    width?: string;
    sortable?: boolean;
    compareFn?: (value1: any, value2: any) => number;
    render?: (row: any) => JSX.Element;
}

interface RowData {
    id: string;
    [key: string]: any;
}

export interface DataTableRowDefinition {
    data: RowData;
    className?: string;
    dataId?: string;
}

export interface DataTableProps {
    columns: DataTableColumnDefinition[];
    rows: DataTableRowDefinition[];
    defaultSortColumn?: string;
    defaultSortDirection?: SortDirection;
    rowHeight?: string;
    headerFontSize?: string;
    dataFontSize?: string;
    loading?: boolean;
    ariaLabel?: string;
    dataId?: string;
    pinnedRows?: string[];
    rowsToShow?: number;
}

export enum SortDirection {
    Ascending = 'ASCENDING',
    Descending = 'DESCENDING',
}

interface StyledHeaderRowProps {
    fontSize?: string;
}

interface StyledHeaderCellProps {
    width?: string;
}

interface StyledRowProps {
    height?: string;
    fontSize?: string;
}

const StyledDataTable = styled.table`
    width: 100%;
    table-layout: fixed;
    border-collapse: separate;
`;

const StyledHeaderRow = styled.tr<StyledHeaderRowProps>`
    font-size: ${(props: StyledHeaderRowProps) => props.fontSize};
    font-weight: 400;
    color: #031c44;
`;

const StyledHeaderCell = styled.th<StyledHeaderCellProps>`
    padding: 15px ${SPACING.MEDIUM};
    text-align: left;
    vertical-align: bottom;
    word-wrap: break-word;
    line-height: 1.5;
    ${(props: StyledHeaderCellProps) => (props.width ? `width: ${props.width}` : '')};
`;

const StyledRow = styled.tr<StyledRowProps>`
    font-size: ${(props: StyledRowProps) => props.fontSize};
    color: #031c44;
    background-color: ${COLOR.PRIMARY.WHITE};
    height: ${(props: StyledRowProps) => props.height};
`;

interface StyledCellProps {
    inFirstRow: boolean;
    inLastRow: boolean;
    inFirstColumn: boolean;
    inLastColumn: boolean;
}

const StyledCell = styled.td<StyledCellProps>`
    padding: 0 ${SPACING.MEDIUM};
    vertical-align: middle;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    // border
    border-color: #cdd3de;
    border-style: solid;
    border-left-width: ${(props) => (props.inFirstColumn ? '1px' : '0')};
    border-right-width: ${(props) => (props.inLastColumn ? '1px' : '0')};
    border-bottom-width: 1px;
    border-top-width: ${(props) => (props.inFirstRow ? '1px' : '0')};
    border-top-left-radius: ${(props) => (props.inFirstRow && props.inFirstColumn ? '5px' : '0')};
    border-top-right-radius: ${(props) => (props.inFirstRow && props.inLastColumn ? '5px' : '0')};
    border-bottom-left-radius: ${(props) => (props.inLastRow && props.inFirstColumn ? '5px' : '0')};
    border-bottom-right-radius: ${(props) => (props.inLastRow && props.inLastColumn ? '5px' : '0')};
`;

const StyledNonSortableHeaderCell = styled.div`
    display: flex;
    align-items: center;
    min-height: 1.4em;
`;

const StyledSortableHeaderCell = styled.div`
    display: flex;
    column-gap: ${SPACING.XX_SMALL};
    align-items: center;
    cursor: pointer;
    user-select: none;

    &:hover {
        text-decoration: underline;
        text-underline-position: under;
    }
`;

const StyledSortIcon = styled.div`
    align-self: flex-end;
    flex-shrink: 0;
    height: 1.4em;
    width: 1.4em;
`;

const StyledHeaderCellText = styled.div`
    user-select: text;
`;

const StyledLoadingSpinner = styled(LoadingSpinner)`
    display: flex;
    justify-content: center;
`;

const StyledTableWrapper = styled.div<{ height?: number }>`
    overflow: hidden;
    transition: height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    height: ${(props) => (props.height !== undefined ? `${props.height}px` : 'auto')};
`;

const DataTableCell = ({
    row,
    column,
    dataId,
    inFirstRow,
    inFirstColumn,
    inLastRow,
    inLastColumn,
}: {
    row: DataTableRowDefinition;
    column: DataTableColumnDefinition;
    dataId?: string;
    inFirstRow: boolean;
    inFirstColumn: boolean;
    inLastRow: boolean;
    inLastColumn: boolean;
}) => {
    return (
        <StyledCell
            data-test-id={dataId}
            inFirstRow={inFirstRow}
            inFirstColumn={inFirstColumn}
            inLastRow={inLastRow}
            inLastColumn={inLastColumn}
        >
            {column.render ? column.render(row.data) : row.data[column.name] ?? ''}
        </StyledCell>
    );
};

const SortableHeaderCell = ({
    column,
    isCurrentSortColumn,
    sortDirection,
    changeSortHandler,
}: {
    column: DataTableColumnDefinition;
    isCurrentSortColumn: boolean;
    sortDirection: SortDirection;
    changeSortHandler: (columnName: string) => void;
}) => {
    return (
        <StyledSortableHeaderCell
            role={'button'}
            aria-label={`Change sort for ${column.header}`}
            onClick={() => changeSortHandler(column.name)}
            data-test-id={
                column.dataId
                    ? `${column.dataId}-header-button-sort`
                    : `${camelToKebab(column.header)}-header-button-sort`
            }
        >
            {!column.hideHeader && <StyledHeaderCellText>{column.header}</StyledHeaderCellText>}
            <StyledSortIcon>
                {isCurrentSortColumn && sortDirection === SortDirection.Ascending && (
                    <NavArrowUp width={'100%'} height={'100%'} />
                )}
                {isCurrentSortColumn && sortDirection === SortDirection.Descending && (
                    <NavArrowDown width={'100%'} height={'100%'} />
                )}
                {!isCurrentSortColumn && <ArrowSeparateVertical width={'100%'} height={'100%'} color={'#9BA8BD'} />}
            </StyledSortIcon>
        </StyledSortableHeaderCell>
    );
};

export const DataTable = React.forwardRef<HTMLTableElement, DataTableProps>(
    (
        {
            columns = [],
            rows = [],
            defaultSortColumn,
            defaultSortDirection = SortDirection.Ascending,
            rowHeight = '80px',
            headerFontSize = '13px',
            dataFontSize = '14px',
            loading,
            dataId,
            ariaLabel,
            pinnedRows = [],
            rowsToShow,
            ...props
        },
        ref,
    ) => {
        const [sortColumnName, setSortColumnName] = useState<string | null>(defaultSortColumn);
        const [sortDirection, setSortDirection] = useState<SortDirection | null>(defaultSortDirection);
        const [tableHeight, setTableHeight] = useState<number | undefined>(undefined);
        const tableRef = useRef<HTMLTableElement>(null);

        const sort = useCallback(() => {
            const rowsClone = cloneDeep(rows);

            if (!sortColumnName) {
                return rowsClone;
            }
            const sortColumn = columns.find((column) => column.name === sortColumnName);

            if (!sortColumn) {
                return rowsClone;
            }

            const multiplier = sortDirection === SortDirection.Descending ? -1 : 1;

            const defaultCompareFn = (a: any, b: any) => {
                const valueA = a[sortColumn.name];
                const valueB = b[sortColumn.name];

                if (typeof valueA === 'string' && typeof valueB === 'string') {
                    return stringCompare(valueA, valueB);
                }

                return anyCompare(valueA, valueB);
            };

            const sortFn = (rowA: DataTableRowDefinition, rowB: DataTableRowDefinition) => {
                const result = sortColumn.compareFn
                    ? sortColumn.compareFn(rowA.data, rowB.data)
                    : defaultCompareFn(rowA.data, rowB.data);

                return result * multiplier;
            };

            // Calling the sort function on cloned rows, hence mutating the array
            rowsClone.sort(sortFn);

            if (pinnedRows) {
                const pinnedElements = rowsClone.filter((row) => pinnedRows.includes(row.data.id));
                const nonPinnedElements = rowsClone.filter((row) => !pinnedRows.includes(row.data.id));

                const pinnedSortedRows = [...pinnedElements, ...nonPinnedElements];

                return pinnedSortedRows;
            } else {
                return rowsClone;
            }
        }, [sortColumnName, sortDirection, rows, columns, pinnedRows]);

        const [sortedRows, setSortedRows] = useState<DataTableRowDefinition[]>(sort());

        const changeSort = (selectedColumnName: string) => {
            const newSortDirection =
                sortColumnName === selectedColumnName && sortDirection === SortDirection.Ascending
                    ? SortDirection.Descending
                    : SortDirection.Ascending;

            setSortColumnName(selectedColumnName);
            setSortDirection(newSortDirection);
        };

        const mappedRows = useMemo(() => {
            const visibleRows = rowsToShow ? sortedRows.slice(0, rowsToShow) : sortedRows;

            return visibleRows.map((row, rowIndex) => {
                return (
                    <StyledRow
                        key={row.data.id}
                        height={rowHeight}
                        fontSize={dataFontSize}
                        className={row.className}
                        data-test-id={row.dataId}
                    >
                        {columns.map((column, columnIndex) => (
                            <DataTableCell
                                key={`${column.name}-${row.data.id}}`}
                                row={row}
                                column={column}
                                dataId={row.dataId ? `${row.dataId}-${camelToKebab(column.header)}` : undefined}
                                inFirstRow={rowIndex === 0}
                                inFirstColumn={columnIndex === 0}
                                inLastRow={rowIndex === visibleRows.length - 1}
                                inLastColumn={columnIndex === columns.length - 1}
                            />
                        ))}
                    </StyledRow>
                );
            });
        }, [columns, dataFontSize, rowHeight, sortedRows, rowsToShow]);

        useEffect(() => {
            setSortColumnName(defaultSortColumn);
            setSortDirection(defaultSortColumn ? defaultSortDirection ?? SortDirection.Ascending : null);
        }, [defaultSortColumn, defaultSortDirection]);

        useDeepCompareEffect(() => {
            const newRows = sort();
            setSortedRows(newRows);
        }, [columns, rows, sortColumnName, sortDirection, pinnedRows]);

        useEffect(() => {
            if (tableRef.current) {
                setTableHeight(tableRef.current.offsetHeight);
            }
        }, [mappedRows]);

        return (
            <>
                {loading ? (
                    <StyledLoadingSpinner color={'black'} />
                ) : (
                    <StyledTableWrapper height={tableHeight}>
                        <StyledDataTable ref={tableRef} data-test-id={dataId} aria-label={ariaLabel} {...props}>
                            <thead>
                                <StyledHeaderRow fontSize={headerFontSize}>
                                    {columns.map((column) => (
                                        <StyledHeaderCell
                                            width={column.width}
                                            key={column.name}
                                            data-test-id={
                                                column.dataId
                                                    ? `${column.dataId}-header`
                                                    : `${camelToKebab(column.header)}-header`
                                            }
                                            {...(column.name === sortColumnName && {
                                                'aria-sort':
                                                    sortDirection === SortDirection.Ascending
                                                        ? 'ascending'
                                                        : 'descending',
                                            })}
                                        >
                                            {column.sortable ? (
                                                <SortableHeaderCell
                                                    column={column}
                                                    isCurrentSortColumn={column.name === sortColumnName}
                                                    sortDirection={sortDirection ?? SortDirection.Ascending}
                                                    changeSortHandler={changeSort}
                                                />
                                            ) : (
                                                !column.hideHeader && (
                                                    <StyledNonSortableHeaderCell>
                                                        {column.header}
                                                    </StyledNonSortableHeaderCell>
                                                )
                                            )}
                                        </StyledHeaderCell>
                                    ))}
                                </StyledHeaderRow>
                            </thead>
                            <tbody>{mappedRows}</tbody>
                        </StyledDataTable>
                    </StyledTableWrapper>
                )}
            </>
        );
    },
);
DataTable.displayName = 'DataTable';
